import React, { ComponentProps, useMemo } from 'react';
import clsx from 'clsx';
import {
  AriaCalendarCellProps,
  AriaCalendarGridProps,
  useCalendarCell,
  useCalendarGrid,
} from '@react-aria/calendar';
import { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import {
  CalendarDate,
  DateValue,
  endOfMonth,
  endOfWeek,
  getDayOfWeek,
  getLocalTimeZone,
  getWeeksInMonth,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  today,
} from '@internationalized/date';
import {
  useDateFormatter,
  useLocale,
  useMessageFormatter,
} from '@react-aria/i18n';
import intlMessages from '../i18n';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import { CalendarDayButton } from './CalendarButtons';
import { sentenceCase } from './utils';
import { filterDOMProps } from '@react-aria/utils';
import { GlobalDomAttributes } from '../../utils/types';

interface CalendarDaysGridProps
  extends Pick<AriaCalendarGridProps, 'startDate'>,
    GlobalDomAttributes {
  state: CalendarState | RangeCalendarState;
  showWeekNumbers?: boolean;
  showFixedWeeks?: boolean;
}

function _CalendarDaysGrid(
  {
    state,
    startDate = state.visibleRange.start,
    showFixedWeeks = false,
    showWeekNumbers,
    ...props
  }: CalendarDaysGridProps,
  ref: React.ForwardedRef<HTMLTableElement>
): JSX.Element {
  const { locale } = useLocale();
  const { gridProps, headerProps } = useCalendarGrid(
    {
      ...props,
      startDate,
      endDate: endOfMonth(startDate),
    },
    state
  );
  const weeksInMonth = showFixedWeeks ? 6 : getWeeksInMonth(startDate, locale);

  const dayFormatter = useDateFormatter({
    weekday: 'short',
    timeZone: state.timeZone,
  });
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(today(state.timeZone), locale);
    return [...new Array(7).keys()].map((index) => {
      const date = weekStart.add({ days: index });
      const dateDay = date.toDate(state.timeZone);
      return dayFormatter.format(dateDay);
    });
  }, [locale, state.timeZone, dayFormatter]);

  return (
    <table
      ref={ref}
      {...gridProps}
      {...filterDOMProps(props, { global: true })}
      className={clsx(styles['sapphire-calendar__table'], {
        [styles['sapphire-calendar__table--with-week-numbers']]:
          showWeekNumbers,
      })}
    >
      <thead {...headerProps}>
        <tr>
          {showWeekNumbers && (
            <th
              className={styles['sapphire-calendar__table-head-cell']}
              aria-hidden="true"
            ></th>
          )}
          {weekDays.map((day, index) => (
            <th
              key={index}
              className={styles['sapphire-calendar__table-head-cell']}
            >
              <abbr className={styles['sapphire-calendar__day-of-week']}>
                {sentenceCase(day)}
              </abbr>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => {
          return (
            <tr
              className={styles['sapphire-calendar__table-body-row']}
              key={weekIndex}
            >
              {showWeekNumbers && (
                <CalendarWeekNumberCell
                  date={startDate.add({ days: 7 * weekIndex })}
                  month={startDate.month}
                />
              )}
              {state.getDatesInWeek(weekIndex, startDate).map((date, i) =>
                date ? (
                  <CalendarCell
                    currentMonth={startDate}
                    // by passing date.toString() instead of index, we make sure the cell
                    // is remounted as we navigate between months. That prevents awkward
                    // color transition if the same day-of-the-month is highlighted in one month
                    // but not the other.
                    key={date.toString()}
                    state={state}
                    date={date}
                  />
                ) : (
                  <td key={i} />
                )
              )}
            </tr>
          );
        })}
        <tr></tr>
      </tbody>
    </table>
  );
}

export const CalendarDaysGrid = React.forwardRef(_CalendarDaysGrid);

function CalendarWeekNumberCell({
  date,
  month,
}: {
  date: DateValue;
  month: number;
}) {
  const { locale } = useLocale();
  const weekNumber = getWeekNumber(date);
  const weekOutsideCurrentMonth =
    startOfWeek(date, locale).month !== month &&
    endOfWeek(date, locale).month !== month;
  const formatMessage = useMessageFormatter(intlMessages);
  return (
    <th
      className={styles['sapphire-calendar__table-body-cell']}
      role="rowheader"
      aria-label={formatMessage('week', { weekNumber })}
      aria-hidden={weekOutsideCurrentMonth}
    >
      {weekOutsideCurrentMonth ? null : (
        <span className={styles['sapphire-calendar__week-number']}>
          {weekNumber}
        </span>
      )}
    </th>
  );
}

function CalendarCell({
  state,
  date,
  currentMonth,
}: {
  state: CalendarState | RangeCalendarState;
  currentMonth: CalendarDate;
} & AriaCalendarCellProps) {
  const ref = React.useRef(null);
  const { locale } = useLocale();
  const sameMonth = isSameMonth(date, currentMonth);
  const {
    cellProps,
    buttonProps,
    isSelected: isInSelection,
    isDisabled,
    formattedDate,
    isUnavailable,
  } = useCalendarCell({ date, isDisabled: !sameMonth }, state, ref);

  const highlightedRange =
    'highlightedRange' in state ? state.highlightedRange : null;

  const isSelected =
    isInSelection &&
    // if we have a highlighted range, only start and end of the range are styled selected.
    (!highlightedRange ||
      isSameDay(highlightedRange.start, date) ||
      isSameDay(highlightedRange.end, date));

  return (
    <td {...cellProps} className={styles['sapphire-calendar__table-body-cell']}>
      <CalendarDayButton
        {...buttonProps}
        ref={ref}
        isSelected={isSelected}
        isDisabled={isDisabled || isUnavailable}
        hasCurrentIndicator={today(state.timeZone).compare(date) === 0}
        isOutsideVisibleRange={!isSameMonth(currentMonth, date)}
        rangeHighlight={
          highlightedRange
            ? getRangeHighlight({ date, highlightedRange, locale })
            : { highlighted: false }
        }
      >
        {formattedDate}
      </CalendarDayButton>
    </td>
  );
}

/**
 * Given a date and a highlighted range, returns the range highlight state for that date.
 * Note that "start" doesn't simply mean start of the range, but means range start **style**,
 * which applies to some dates inside the range as well. A css-only solution could also cover
 * this, but it would require `:has()` selector which is not widely supported at the time of writing
 * this.
 */
const getRangeHighlight = ({
  date,
  highlightedRange,
  locale,
}: {
  date: DateValue;
  highlightedRange: RangeCalendarState['highlightedRange'];
  locale: string;
}): ComponentProps<typeof CalendarDayButton>['rangeHighlight'] => {
  const isInSelection =
    !!highlightedRange &&
    highlightedRange.start.compare(date) <= 0 &&
    highlightedRange.end.compare(date) >= 0;
  const rangeIsNotASingleDay =
    !!highlightedRange &&
    !isSameDay(highlightedRange.start, highlightedRange.end);
  const isStartOfWeek = getDayOfWeek(date, locale) === 0;
  const isEndOfWeek = getDayOfWeek(date, locale) === 6;
  const isStartOfMonth = isSameDay(date, startOfMonth(date));
  const isEndOfMonth = isSameDay(date, endOfMonth(date));
  const isStartOfRange =
    highlightedRange && isSameDay(highlightedRange?.start, date);
  const isEndOfRange =
    highlightedRange && isSameDay(highlightedRange?.end, date);

  const highlighted = isInSelection && rangeIsNotASingleDay;
  return {
    highlighted,
    start: highlighted && (isStartOfWeek || isStartOfMonth || isStartOfRange),
    end: highlighted && (isEndOfWeek || isEndOfMonth || isEndOfRange),
  };
};

/**
 * Almost copied from https://weeknumber.com/how-to/javascript
 * No corresponding API found in @internationalized/date package, at the time
 * of writing this.
 * Caveat: this code is based on built-in Date object, and may not be
 * calendar-agnostic. We don't really care about supporting different types
 * of calendar, so it should be fine for now. But we can look into improving
 * this if needed.
 */
const getWeekNumber = function (dateValue: DateValue) {
  const date = new Date(dateValue.toDate(getLocalTimeZone()));
  date.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  // January 4 is always in week 1.
  const week1 = new Date(date.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count number of weeks from date to week1.
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    )
  );
};
